import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {HttpClient} from "@angular/common/http";
import {NgSwitch, NgSwitchCase} from '@angular/common';
import * as QRCode from 'qrcode';
import {environment} from "../../environments/environment";

type Platform = 'ios' | 'android' | 'desktop';

@Component({
    selector: 'install',
    templateUrl: './install.component.html',
    styleUrls: ['./install.component.css'],
    imports: [NgSwitch, NgSwitchCase]
})
export class InstallComponent implements OnInit {
    private static readonly APP_STORE_URL = 'https://apps.apple.com/us/app/id1604760776';
    private static readonly PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.club15';

    isLoading: boolean = false;

    platform: Platform = 'desktop';

    desktopInstallQrData: string | null = null;

    private sentIosAttributionEvent: boolean = false;

    constructor(private route: ActivatedRoute, private httpClient: HttpClient) {

    }

    ngOnInit(): void {
        this.platform = this.detectPlatform();

        if (this.platform === 'android') {
            this.redirectTo(this.getAndroidInstallUrl());
            return;
        }

        if (this.platform === 'desktop') {
            const url = window.location.href;
            void this.generateDesktopQr(url)
                .then(data => { this.desktopInstallQrData = data; });
        }
    }

    getIosInstallUrl(): string {
        const queryParams = this.buildQueryParams();
        return `${InstallComponent.APP_STORE_URL}${queryParams ? `?${queryParams}` : ''}`;
    }

    getAndroidInstallUrl(): string {
        const qp = this.buildQueryParams();
        return `${InstallComponent.PLAY_STORE_URL}${qp ? `&referrer=${encodeURIComponent(qp)}` : ''}`;
    }

    startIosInstall(copyLink: boolean): void {
        this.isLoading = true;
        const rawQueryParams = this.buildQueryParams();
        const clickId = this.createClickId();
        if (copyLink) {
            this.copyAttributionUrlToClipboard(rawQueryParams, clickId);
        }
        this.trackIosClickAndRedirect(rawQueryParams, clickId);
    }

    private trackIosClickAndRedirect(rawQueryParams: string, clickId: string): void {
        const url = this.getIosInstallUrl();

        if (this.sentIosAttributionEvent) {
            this.redirectTo(url);
            return;
        }

        const payload = {
            clickId: clickId,
            queryParams: rawQueryParams,
            locale: (navigator.languages && navigator.languages.length > 0 ? navigator.languages[0] : (navigator.language || '')) || null,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
            screenWidth: (window.screen && Number.isFinite(window.screen.width)) ? Math.round(window.screen.width * window.devicePixelRatio) : null,
            screenHeight: (window.screen && Number.isFinite(window.screen.height)) ? Math.round(window.screen.height * window.devicePixelRatio) : null
        };

        this.httpClient
            .post(`${environment.getAttributionBaseUrl()}/rest/attribution/click`, payload, {responseType: 'text'})
            .subscribe({
                next: () => { this.sentIosAttributionEvent = true; this.isLoading = false; this.redirectTo(url); },
                error: () => { this.sentIosAttributionEvent = true; this.isLoading = false; this.redirectTo(url); },
            });
    }

    private copyAttributionUrlToClipboard(rawQueryParams: string, clickId: string): void {
        const clickIdB64 = this.base64UrlEncode(clickId);
        const rawParamsB64 = this.base64UrlEncode(rawQueryParams);
        const clipboardValue = `${window.location.origin}/?click=${clickIdB64}&params=${rawParamsB64}`;
        void this.copyToClipboard(clipboardValue);
    }

    private createClickId(): string {
        return globalThis.crypto?.randomUUID?.() ?? `cid-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;
    }

    private redirectTo(url: string): void {
        window.location.href = url;
    }

    private detectPlatform(): Platform {
        const userAgent = navigator.userAgent;
        if (/Android/i.test(userAgent)) {
            return 'android';
        }
        if (/iPad|iPhone|iPod/.test(userAgent)) {
            return 'ios';
        }
        return 'desktop';
    }

    private async generateDesktopQr(url: string): Promise<string | null> {
        try {
            return await QRCode.toDataURL(url, { width: 240, margin: 1 });
        } catch {
            return null;
        }
    }

    private buildQueryParams(): string {
        const paramMap = this.route.snapshot.queryParamMap;
        const parts: string[] = [];
        paramMap.keys.forEach(key => {
            const value = paramMap.get(key);
            if (value != null) {
                parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
            }
        });
        return parts.join('&');
    }

    private base64UrlEncode(value: string): string {
        const base64 = btoa(value);
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/,'');
    }

    private async copyToClipboard(text: string): Promise<boolean> {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                return true;
            }

            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.readOnly = true;
            textarea.style.cssText = 'position:fixed;top:-9999px;opacity:0;';
            document.body.appendChild(textarea);
            textarea.select();
            (document as any).execCommand('copy');
            document.body.removeChild(textarea);
            return true;
        } catch (err) {
            console.error('Clipboard copy failed:', err);
            return false;
        }
    }
}
